import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, combineLatest, merge, Subject, timer } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class UpdateCheckService {
  // private checkInterval = 1000 * 60 * 15; // 15 dakika
  private checkInterval = 1000 * 60 * 1; // 1 dakika
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);
  private updateDeferredSubject = new BehaviorSubject<boolean>(false);
  private checkingForUpdateSubject = new Subject<boolean>();
  private readonly versionKey = 'rtls.app.version';
  private readonly authTokenKey = 'badis_oauth_token';
  private readonly deferredKey = 'rtls.update.deferred';
  private readonly assetRecoveryKey = 'rtls.asset-load-recovery';
  private applyNotBefore = 0;

  updateAvailable$ = this.updateAvailableSubject.asObservable();
  updateDeferred$ = this.updateDeferredSubject.asObservable();
  pendingUpdate$ = combineLatest([this.updateAvailable$, this.updateDeferred$]).pipe(
    map(([available, deferred]) => available || deferred)
  );
  checkingForUpdate$ = this.checkingForUpdateSubject.asObservable();

  constructor(private swUpdate: SwUpdate) {
    void this.resetIfVersionChanged();
    this.installAssetLoadRecovery();
    this.updateDeferredSubject.next(this.readDeferredFlag());

    /*
     * DEMO: Geçici update tetikleyici (test sonrası sil)
     * Kullanım: localStorage.setItem('rtls.update.demo','1') ve sayfayı yenile.
     */
    // if (localStorage.getItem('rtls.update.demo') === '1') {
    //   localStorage.removeItem('rtls.update.demo');
    //   setTimeout(() => this.updateAvailableSubject.next(true), 1000);
    // }

    if (swUpdate.isEnabled) {
      const appStartCheck$ = timer(10000, this.checkInterval);
      const updatesAvailable$ = this.swUpdate.versionUpdates.pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        map(evt => evt.currentVersion)
      );

      merge(appStartCheck$, updatesAvailable$).subscribe(() => this.checkForUpdates());
    }
  }

  public async checkForUpdates(): Promise<void> {
    if (this.updateAvailableSubject.value || this.updateDeferredSubject.value) {
      return;
    }

    if (!this.swUpdate.isEnabled) {
      // this.updateAvailableSubject.next(true);
      return;
    }

    console.log('Güncelleme kontrol ediliyor...');

    this.checkingForUpdateSubject.next(true);
    try {
      const hasUpdate = await this.swUpdate.checkForUpdate();
      this.checkingForUpdateSubject.next(false);
      if (hasUpdate) {
        console.log('Yeni bir güncelleme mevcut!');
        if (this.readDeferredFlag()) {
          this.updateDeferredSubject.next(true);
          this.updateAvailableSubject.next(false);
          return;
        }
        this.updateAvailableSubject.next(true);
        return;
      }

      if (this.readDeferredFlag()) {
        this.clearDeferredFlag();
        this.updateDeferredSubject.next(false);
      }
      if (!this.updateAvailableSubject.value) {
        this.updateAvailableSubject.next(false);
      }
    } catch (err) {
      console.error('Güncelleme kontrolü sırasında hata oluştu:', err);
      this.checkingForUpdateSubject.next(false);
    }
  }

  public applyUpdate() {
    const delay = this.applyNotBefore - Date.now();
    const waitFor = delay > 0 ? delay : 0;
    setTimeout(() => {
      this.swUpdate.activateUpdate().then(async () => {
        this.clearDeferredFlag();
        this.updateAvailableSubject.next(false);
        this.updateDeferredSubject.next(false);
        await this.purgeCaches(environment.version);
        // Unregister stale service workers before reload to guarantee clean re-fetch
        await this.unregisterAllServiceWorkers();
        document.location.reload();
      });
    }, waitFor);
  }

  public deferUpdate(): void {
    this.storeDeferredFlag(true);
    this.updateAvailableSubject.next(false);
    this.updateDeferredSubject.next(true);
  }

  public resumeDeferredUpdate(): void {
    if (!this.readDeferredFlag()) {
      return;
    }

    this.clearDeferredFlag();
    this.updateDeferredSubject.next(false);
    this.updateAvailableSubject.next(true);
    void this.checkForUpdates();
  }

  public registerUpdateSequenceWindow(durationMs: number): void {
    if (!Number.isFinite(durationMs) || durationMs <= 0) {
      this.applyNotBefore = 0;
      return;
    }
    this.applyNotBefore = Date.now() + durationMs;
  }

  private async resetIfVersionChanged(): Promise<void> {
    const currentVersion = environment.version ?? '0';
    const storedVersion = localStorage.getItem(this.versionKey);
    if (storedVersion === currentVersion) {
      return;
    }

    this.clearDeferredFlag();
    await this.purgeCaches(currentVersion);
  }

  private async purgeCaches(nextVersion?: string): Promise<void> {
    const authToken = localStorage.getItem(this.authTokenKey);
    const deferredFlag = this.readDeferredFlag();
    const assetRecoveryFlag = sessionStorage.getItem(this.assetRecoveryKey);

    try {
      localStorage.clear();
      sessionStorage.clear();
      if (authToken) {
        localStorage.setItem(this.authTokenKey, authToken);
      }
      if (deferredFlag) {
        localStorage.setItem(this.deferredKey, '1');
      }
      if (nextVersion) {
        localStorage.setItem(this.versionKey, nextVersion);
      }
      if (assetRecoveryFlag) {
        sessionStorage.setItem(this.assetRecoveryKey, assetRecoveryFlag);
      }
    } catch (error) {
      console.error('Storage clear failed:', error);
    }

    if ('caches' in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      } catch (error) {
        console.error('Cache clear failed:', error);
      }
    }

    if ('indexedDB' in window && 'databases' in indexedDB) {
      try {
        const dbs = await indexedDB.databases();
        await Promise.all(
          dbs.map((db) => {
            if (!db.name) {
              return Promise.resolve();
            }
            return new Promise<void>((resolve) => {
              const req = indexedDB.deleteDatabase(db.name as string);
              req.onsuccess = () => resolve();
              req.onerror = () => resolve();
              req.onblocked = () => resolve();
            });
          })
        );
      } catch (error) {
        console.error('IndexedDB clear failed:', error);
      }
    }
  }

  private readDeferredFlag(): boolean {
    return localStorage.getItem(this.deferredKey) === '1';
  }

  private storeDeferredFlag(enabled: boolean): void {
    if (enabled) {
      localStorage.setItem(this.deferredKey, '1');
      return;
    }
    localStorage.removeItem(this.deferredKey);
  }

  private clearDeferredFlag(): void {
    this.storeDeferredFlag(false);
  }

  private async unregisterAllServiceWorkers(): Promise<void> {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    } catch (e) {
      console.error('SW unregister failed:', e);
    }
  }

  private installAssetLoadRecovery(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      void this.recoverFromStaleAssetLoad(event.reason);
    });

    window.addEventListener('error', (event: ErrorEvent) => {
      void this.recoverFromStaleAssetLoad(event.error ?? event.message);
    });
  }

  private async recoverFromStaleAssetLoad(error: unknown): Promise<void> {
    if (!this.isRecoverableAssetLoadError(error)) {
      return;
    }

    if (sessionStorage.getItem(this.assetRecoveryKey) === '1') {
      return;
    }

    sessionStorage.setItem(this.assetRecoveryKey, '1');
    await this.purgeCaches(environment.version);
    await this.unregisterAllServiceWorkers();
    window.location.reload();
  }

  private isRecoverableAssetLoadError(error: unknown): boolean {
    const message = this.stringifyError(error);
    return /ChunkLoadError|Loading chunk|dynamically imported module|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|\.module-[a-z0-9]+\.js/i.test(message);
  }

  private stringifyError(error: unknown): string {
    if (!error) {
      return '';
    }

    if (typeof error === 'string') {
      return error;
    }

    if (error instanceof Error) {
      return `${error.name} ${error.message} ${error.stack ?? ''}`;
    }

    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
}
